import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Grid, Box, CircularProgress, Card, Pagination } from '@mui/material';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { EditButton, GroupSelectionButton } from '../../../component/buttons';
import { useRole } from '../../../utils/RoleContext';
import { getPanelRole } from '../../../services/api';
import { useUserGroups } from '../../../hooks/useGroups';
import GroupCard from './components/GroupCard';

const getProgress = (start, end) => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const today = dayjs();

    const total = endDate.diff(startDate, 'day');
    const current = today.diff(startDate, 'day');

    if (current <= 0) return 0;
    if (current >= total) return 100;

    return Math.round((current / total) * 100);
};

const Groups = () => {
    const navigate = useNavigate();
    const roleUUID = getPanelRole();
    const { role } = useRole();

    const [selected, setSelected] = useState(0);
    const status = selected === 0 ? true : false;

    const {
        data: groups,
        isLoading,
        error,
        count,
        paginationModel,
        setPaginationModel
    } = useUserGroups(roleUUID, status);

    const handlePageChange = (_, newPage) => {
        setPaginationModel((prev) => ({ ...prev, page: newPage - 1 }));
    };

    const sections = [t('groups.sections_active'), t('groups.sections_completed')];

    return (
        <Card
            variant="outlined"
            sx={{ p: 4, m: 2, borderRadius: 3, transition: 'all 0.3s ease' }}
        >
            <Grid container spacing={2}>
                {/* Toggle buttons */}
                {sections.map((label, index) => (
                    <Grid item xs={6} key={index}>
                        <GroupSelectionButton
                            selected={selected === index}
                            colorType={index === 0 ? 'active' : 'finished'}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => setSelected(index)}
                        >
                            {label}
                        </GroupSelectionButton>
                    </Grid>
                ))}

                {/* Add new group button */}
                <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <EditButton onClick={() => navigate('/grupos/criar')}>
                        <Typography variant="subtitle2">
                            {t('groups.add_new_group')}
                        </Typography>
                    </EditButton>
                </Grid>

                {/* Groups list */}
                {isLoading ? (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" height="300px">
                            <CircularProgress sx={{ color: '#1976d2' }} />
                        </Box>
                    </Grid>
                ) : error ? (
                    <Grid item xs={12}>
                        <Typography color="error" align="center">
                            {t('groups.load_error')}
                        </Typography>
                    </Grid>
                ) : groups.length > 0 ? (
                    <>
                        {groups.map((group) => (
                            <Grid item xs={12} md={6} key={group.uuid}>
                                <GroupCard
                                    group={group}
                                    onView={
                                        role === 'DOCTOR'
                                            ? () => navigate(`/grupos/${group.uuid}`)
                                            : undefined
                                    }
                                    onEdit={
                                        role === 'DOCTOR'
                                            ? () =>
                                                  navigate(`/grupos/editar/${group.uuid}`)
                                            : undefined
                                    }
                                    isFinished={selected === 1}
                                />
                            </Grid>
                        ))}

                        {/* Pagination */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={4}>
                                <Pagination
                                    count={Math.ceil(count / paginationModel.pageSize)}
                                    page={paginationModel.page + 1}
                                    onChange={handlePageChange}
                                />
                            </Box>
                        </Grid>
                    </>
                ) : (
                    <Grid item xs={12}>
                        <Typography variant="body1" color="text.secondary" align="center">
                            {selected === 0
                                ? t('groups.no_groups.active')
                                : t('groups.no_groups.completed')}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Card>
    );
};

export default Groups;
